import { useEffect, useRef, useState } from 'react'
import type { AgentV2ArtifactEditorFlushResult } from '@/agent-v2/artifact-editor/components/AgentV2ArtifactEditor'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { postDocxExport } from '@/agent-v2/docx-export/fetch/post-docx-export'
import { FileDownload } from '@/assets/icons'
import { SelectButton } from '@/components/select-button/SelectButton'
import { GlobalToastType, kToastDurationLongMs } from '@/constants/constants-ui'
import { kFlushFailedMessage } from '@/matter-artifacts/constants'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ApiError } from '@/util/errors/api-error'

export type ExportDocxButtonProps = {
  conversationId: string
  matterArtifactVersionId: string
  templateUserFileId: string
  /** Saves unsaved editor changes first, so the export uses the draft as the user sees it. */
  prepareExport: () => Promise<AgentV2ArtifactEditorFlushResult | null>
}

/**
 * Starts an Export as .docx of the draft with its selected template. The conversation reports the
 * start and the result; while an export of this version and template is open, the button waits.
 */
export function ExportDocxButton({ conversationId, matterArtifactVersionId, templateUserFileId, prepareExport }: ExportDocxButtonProps) {
  const dispatch = useAppDispatch()
  const [isStarting, setIsStarting] = useState(false)
  const hasOpenExport = useAppSelector((state) =>
    Boolean(
      state.agentV2ConversationsState.details[conversationId]?.open_docx_exports?.some(
        (openExport) => openExport.matter_artifact_version_id === matterArtifactVersionId && openExport.template_user_file_id === templateUserFileId
      )
    )
  )
  const isExporting = isStarting || hasOpenExport
  const buttonRef = useRef<HTMLButtonElement>(null)

  // The button stays focusable while it waits, but focus can still fall to the page during a long
  // export, so put it back once the export ends unless the user has moved on themselves.
  const wasExportingRef = useRef(isExporting)
  useEffect(() => {
    const wasExporting = wasExportingRef.current
    wasExportingRef.current = isExporting
    if (!wasExporting || isExporting || document.activeElement !== document.body) return
    buttonRef.current?.focus()
  }, [isExporting])

  const handleClick = async () => {
    if (isExporting) return
    setIsStarting(true)
    try {
      const flushResult = await prepareExport()
      if (flushResult?.status === 'failed') {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kFlushFailedMessage, durationMs: kToastDurationLongMs }))
        return
      }
      const docxExport = await postDocxExport({
        conversationId,
        matterArtifactVersionId: flushResult?.matterArtifactVersionId ?? matterArtifactVersionId,
        templateUserFileId,
        clientRequestId: crypto.randomUUID(),
      })
      // Tracked from the moment the server admits it, so the button stays busy and polling starts
      // even if the reread below fails. That reread brings the started notice; polling continues
      // until the result notice lands.
      dispatch(AgentV2ConversationsActions.docxExportAdmitted({ conversationId, docxExport }))
      dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
    } catch (error) {
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: exportStartErrorMessage(error), durationMs: kToastDurationLongMs }))
      // An export the server admitted but could not start already has its started and failure
      // notices in the conversation.
      dispatch(AgentV2ConversationsActions.fetchConversationDetail({ conversationId, forceRefresh: true }))
    } finally {
      setIsStarting(false)
    }
  }

  const label = isExporting ? 'Exporting…' : 'Export as .docx'
  // The same chip as the template control beside it; the chat card reports progress, not a spinner.
  // aria-disabled rather than disabled, so keyboard focus stays on the button for the whole export.
  return (
    <SelectButton
      ref={buttonRef}
      color="blue"
      leadingIcon={FileDownload}
      aria-label={label}
      aria-disabled={isExporting}
      hideLabelBelow={480}
      className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
      onClick={handleClick}
    >
      {label}
    </SelectButton>
  )
}

/** Only a server or network fault is worth retrying as is; every other refusal says what to change. */
function exportStartErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 400:
        return "This template isn't a usable Word document. Select a different template."
      case 403:
        return 'Only the person who created this draft can export it.'
      case 404:
        return 'This draft or its template is no longer available. Refresh the page or select a different template.'
      case 409:
        return "This version of the draft isn't complete, so it can't be exported."
    }
  }
  return 'The export could not start. Please try again.'
}
